import React, { useRef, useEffect, useContext, useState } from 'react';
import { Container, Row, Col, Button, Modal } from 'reactstrap';
import { HashLink } from 'react-router-hash-link';
import RepoVideo from '../repository/RepoVideo';
import NextVideo from './NextVideo';
import { UserContext } from '../App';
import useVdoPlayer from './useVdoPlayer';
import './Video.css';

export const AUTH_TIMEOUT_MS = 20000;
export const MEDIA_TIMEOUT_MS = 30000;

const Video = (props) => {
  const videoDiv = useRef(null);
  const { myClasses } = useContext(UserContext);
  const [playerStatus, setPlayerStatus] = useState('初始化中...');
  const [canRetry, setCanRetry] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [showAuthError, setShowAuthError] = useState(false);
  const { isAPIReady, apiError, loadVideo } = useVdoPlayer(retryAttempt);
  const { classId } = props.match.params;
  const chapterIdx = parseInt(props.match.params.chapterIdx, 10);
  const sectionIdx = parseInt(props.match.params.sectionIdx, 10);
  const videoIdx = parseInt(props.match.params.videoIdx, 10);
  const targetClass = Array.isArray(myClasses) ? myClasses.find(x => x.id == classId) : null;
  const course = targetClass ? JSON.parse(targetClass.courseData) : null;
  const chapter = course?.chapters[chapterIdx];
  const section = chapter?.sections[sectionIdx];
  const video = section?.videos[videoIdx];
  const videoId = video?.id;

  // A page view is recorded once per navigation, not once per status render.
  useEffect(() => {
    if (videoId == null) return;
    let active = true;
    RepoVideo.sendVideoAction(videoId, 1, classId).catch(error => {
      if (active && error.status === 401) setShowAuthError(true);
    });
    return () => { active = false; };
  }, [videoId, classId, chapterIdx, sectionIdx, videoIdx]);

  useEffect(() => {
    if (videoId == null) return;
    setCanRetry(false);
    if (!isAPIReady) {
      setPlayerStatus(apiError || '正在載入播放器元件...');
      setCanRetry(Boolean(apiError));
      return;
    }

    let disposed = false;
    let acceptingOtp = true;
    let timeout;
    let iframe;
    let player;
    let receivedMediaEvent = false;
    let mediaAvailable = false;
    const listeners = [];
    const controller = new AbortController();
    const clearWatchdog = () => clearTimeout(timeout);
    const showReady = (message) => {
      clearWatchdog();
      setCanRetry(false);
      setPlayerStatus(message);
    };
    const watchMedia = () => {
      clearWatchdog();
      timeout = setTimeout(() => {
        if (disposed) return;
        setPlayerStatus('影片尚未開始或載入較久，請先點擊影片內的播放鍵；若仍無法播放，請檢查網路後重試。');
        setCanRetry(true);
      }, MEDIA_TIMEOUT_MS);
    };
    const recordAction = (actionId) => {
      RepoVideo.sendVideoAction(videoId, actionId, classId).catch(error => {
        if (!disposed && error.status === 401) setShowAuthError(true);
      });
    };
    const on = (event, handler) => {
      const guarded = () => {
        if (disposed) return;
        receivedMediaEvent = true;
        handler();
      };
      player.video.addEventListener(event, guarded);
      listeners.push([event, guarded]);
    };
    const onFrameLoad = () => {
      if (!disposed && !receivedMediaEvent) setPlayerStatus('播放器畫面已開啟，請點擊影片內的播放鍵。');
    };

    async function start() {
      setPlayerStatus('取得影片授權中...');
      timeout = setTimeout(() => {
        if (disposed) return;
        acceptingOtp = false;
        controller.abort();
        setPlayerStatus('取得影片授權逾時，請檢查網路後重試。');
        setCanRetry(true);
      }, AUTH_TIMEOUT_MS);
      try {
        const { otp, vid, playbackInfo } = await RepoVideo.getOtp(videoId, { signal: controller.signal });
        if (disposed || !acceptingOtp) return;
        clearWatchdog();
        if (!otp || !vid) throw new Error('Incomplete video authorization');
        setPlayerStatus('建立播放器，請稍候...');
        iframe = loadVideo({
          otp,
          playbackInfo: playbackInfo || btoa(JSON.stringify({ videoId: vid })),
          container: videoDiv.current,
          configuration: { theme: '9111e0056d664282b368c5558a36f32a' },
        });
        iframe.addEventListener('load', onFrameLoad);
        player = window.VdoPlayer.getInstance(iframe);
        let registered30 = false;
        let registered50 = false;
        let registered90 = false;
        let lastTime = 0;

        on('loadstart', () => setPlayerStatus('開始載入影片...'));
        on('loadedmetadata', () => setPlayerStatus('影片資訊已載入，請點擊影片內的播放鍵。'));
        on('canplay', () => { mediaAvailable = true; showReady('影片已就緒，可以播放'); });
        on('play', () => {
          recordAction(2);
          setPlayerStatus('正在開始播放...');
          watchMedia();
        });
        on('playing', () => { mediaAvailable = true; showReady('播放中'); });
        on('pause', () => {
          if (mediaAvailable) showReady('已暫停');
        });
        on('waiting', () => {
          setPlayerStatus('緩衝中...');
          watchMedia();
        });
        on('seeking', () => {
          setPlayerStatus('跳轉中...');
          watchMedia();
        });
        on('seeked', () => {
          if (player.video.paused && mediaAvailable) showReady('已暫停');
          else {
            setPlayerStatus('正在恢復播放...');
            watchMedia();
          }
        });
        on('ended', () => {
          showReady('播放完畢');
          if (registered30 && registered50 && registered90) [3, 4, 5, 6].forEach(recordAction);
          else recordAction(7);
        });
        on('error', () => {
          clearWatchdog();
          mediaAvailable = false;
          const code = player.video.error?.code;
          setPlayerStatus('影片播放失敗' + (code ? '（錯誤碼 ' + code + '）' : '') + '，請檢查網路後重試。');
          setCanRetry(true);
        });
        on('timeupdate', () => {
          const currentTime = player.video.currentTime;
          const ratio = currentTime / player.video.duration;
          if (player.video.ended) return;
          if (currentTime > lastTime && !player.video.paused && !player.video.seeking) {
            mediaAvailable = true;
            showReady('播放中');
          }
          lastTime = currentTime;
          if (!registered30 && ratio > 0.3 && ratio < 0.35) { registered30 = true; recordAction(3); }
          if (!registered50 && ratio > 0.5 && ratio < 0.55) { registered50 = true; recordAction(4); }
          if (!registered90 && ratio > 0.88 && ratio < 0.92) { registered90 = true; recordAction(5); }
        });
        watchMedia();
      } catch (error) {
        if (disposed || !acceptingOtp) return;
        clearWatchdog();
        const status = error.response?.status || error.status;
        if (status === 401) {
          setPlayerStatus('登入已過期，請重新登入。');
          setShowAuthError(true);
        } else if (status === 403) {
          setPlayerStatus('沒有這部影片的觀看權限，請聯繫課程管理者。');
        } else if (status === 404) {
          setPlayerStatus('找不到這部影片，請聯繫課程管理者。');
        } else {
          setPlayerStatus('影片載入失敗，請檢查網路後重試。');
          setCanRetry(true);
        }
      }
    }
    start();
    return () => {
      disposed = true;
      acceptingOtp = false;
      controller.abort();
      clearWatchdog();
      listeners.forEach(([event, handler]) => {
        const removal = player.video.removeEventListener(event, handler);
        removal?.catch?.(() => {});
      });
      if (iframe) {
        iframe.removeEventListener('load', onFrameLoad);
        iframe.remove();
      }
    };
  }, [videoId, classId, chapterIdx, sectionIdx, videoIdx, isAPIReady, apiError, loadVideo, retryAttempt]);

  const nextVideo = () => {
    if (videoIdx < section.videos.length - 1) {
      return { classId, chapterIdx, sectionIdx, videoIdx: videoIdx + 1, title: section.videos[videoIdx + 1].title };
    }
    if (sectionIdx < chapter.sections.length - 1) {
      return { classId, chapterIdx, sectionIdx: sectionIdx + 1, videoIdx: 0, title: chapter.sections[sectionIdx + 1].videos[0].title };
    }
    if (chapterIdx < course.chapters.length - 1) {
      return { classId, chapterIdx: chapterIdx + 1, sectionIdx: 0, videoIdx: 0, title: course.chapters[chapterIdx + 1].sections[0].videos[0].title };
    }
    return null;
  };

  return !video ? <></> : (
    <Container>
      <Row><Col><h2>{section.name}</h2><h4>{video.title}</h4></Col></Row>
      <Row><Col><div ref={videoDiv} className="vdo-container"></div></Col></Row>
      <Row>
        <Col lg={true} className="d-flex align-items-center">
          <HashLink to={`/${classId}#SEC-${chapterIdx}-${sectionIdx}`} className="me-2">
            <Button className="float-left">^ 回課程大綱</Button>
          </HashLink>
          <NextVideo nextVideo={nextVideo()} />
        </Col>
        <div role="status" aria-live="polite" style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
          狀態：{playerStatus}
        </div>
        {canRetry && <div className="mt-2">
          <Button color="primary" onClick={() => setRetryAttempt(value => value + 1)}>重新載入影片</Button>
        </div>}
      </Row>
      <Modal isOpen={showAuthError} toggle={() => setShowAuthError(false)}>
        <div className="modal-header"><h5 className="modal-title">登入已過期</h5></div>
        <div className="modal-body"><p>您的登入憑證已過期，請重新登入以繼續使用課程平台。</p></div>
        <div className="modal-footer">
          <Button color="primary" onClick={() => { window.location = '/authentication/login'; }}>前往登入頁面</Button>
        </div>
      </Modal>
    </Container>
  );
};

export { Video };
